import React from 'react'
import {
  Application,
  MenuBar,
  MenuBarItemProps
} from '@energytoolbase/components'

import logo from 'assets/etb-logo-white.svg'
import { useNavigate } from 'react-router-dom'
import developerIcon from 'assets/nav/developer-icon.png'
import monitorIcon from 'assets/nav/monitor-icon.png'
import consultingIcon from 'assets/nav/consulting-icon.png'
import productsIconDefault from 'assets/nav/products-icon-default.svg'
import productsIconHover from 'assets/nav/products-icon-hover.svg'
import { faUfoBeam } from '@fortawesome/pro-solid-svg-icons'

import {
  getDeveloperBaseUrl,
  getDeveloperUrl,
  getServicesUrl,
  getMonitorUrl,
  getProfilePictureUrl
} from 'utils/env'
import { componentRoutes } from 'router/routes'
import { useAuth } from 'context/authContext'
import useUser from 'hooks/useUser'
import useCompany from 'hooks/useCompany'
import { EMDASH } from 'utils/constants'
import { isAccessDenied } from 'router/isAccessDenied'
import { insertIf } from 'utils/arrays'
import { useCompanyPlatformAccess } from 'hooks/useCompanyPlatformAccess'
import { Permissions } from 'utils/rolesAndPermissions'

export function UpperNavBar(): JSX.Element {
  // Router
  const navigate = useNavigate()
  const { logout, permissions } = useAuth()

  // Data
  const { data: userData } = useUser()
  const { data: companyData } = useCompany()
  const {
    data: { hasDeveloper, hasMonitor, hasServices }
  } = useCompanyPlatformAccess()

  const firstName = userData?.firstName ?? ''
  const lastName = userData?.lastName ?? ''

  const profilePictureSrc = getProfilePictureUrl(
    userData?.profilePictureGuid,
    'large'
  )

  const companyName = companyData?.companyName ?? EMDASH
  return (
    <Application>
      <MenuBar
        logoItem={{
          img: logo,
          imgHeight: '30px',
          appSwitcherItems: {
            defaultIcon: productsIconDefault,
            hoveredIcon: productsIconHover,
            subTitleText: 'Energy Toolbase Apps',
            options: [
              ...insertIf(hasMonitor, {
                title: 'ETB Monitor',
                icon: monitorIcon,
                href: getMonitorUrl(),
                selected: false
              }),
              ...insertIf(hasDeveloper, {
                title: 'ETB Developer',
                icon: developerIcon,
                href: getDeveloperUrl(),
                selected: false
              }),
              ...insertIf(hasServices, {
                title: 'ETB Consulting',
                icon: consultingIcon,
                href: getServicesUrl(),
                selected: false
              })
            ]
          }
        }}
        rightAlignedItems={[
          {
            iconClass: 'far fa-question-circle',
            onClick: (): void => {
              window
                .open('https://help.energytoolbase.com/', '_blank', 'noopener')
                ?.focus()
            }
          }
        ]}
        userItem={{
          firstName,
          lastName,
          companyName,
          profilePictureSrc: profilePictureSrc,
          subItems: [
            ...componentRoutes
              .filter(
                ({ permissionWhitelist }) =>
                  !isAccessDenied(permissions, permissionWhitelist)
              )
              .map(({ title, route, iconClass }) => ({
                text: title,
                onClick: (): void => navigate(route),
                iconClass
              })),
            ...adminDropDownItems(
              permissions,
              hasDeveloper,
              Boolean(userData?.isRoaming)
            ),
            {
              iconClass: 'flaticon-logout',
              includeDividerBefore: true,
              text: 'Logout',
              onClick: logout
            }
          ]
        }}
      />
    </Application>
  )
}

function adminDropDownItems(
  permissions: readonly string[],
  hasDeveloper: boolean,
  isRoaming: boolean
): readonly MenuBarItemProps[] {
  const canRoamOrEditCompanies =
    hasDeveloper &&
    (permissions.includes(Permissions.etbAdminCanEditCompanies) ||
      permissions.includes(Permissions.etbAdminCanRoamCompanyAccounts))

  const companiesNavItem: MenuBarItemProps = {
    iconClass: 'fa fa-industry',
    onClick: (): void => {
      window.open(`${getDeveloperUrl()}#companies/true`, '_self')
    },
    text: 'Companies',
    isDropdown: true
  }

  const roamHomeNavItem: MenuBarItemProps = {
    iconClass: faUfoBeam,
    text: 'ETB Roam Home',
    onClick: (): void => {
      window.open(`${getDeveloperBaseUrl()}/Roam/Home`, '_self')
    },
    isDropdown: true
  }

  return [
    ...insertIf(canRoamOrEditCompanies, companiesNavItem),
    ...insertIf(
      isRoaming &&
        permissions.includes(Permissions.etbAdminCanRoamCompanyAccounts),
      roamHomeNavItem
    )
  ].map((item, idx) => ({ ...item, includeDividerBefore: idx === 0 }))
}
